import { useEffect, useMemo, useState, useCallback } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import { api, endpoints } from '../api/config';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import { COLUMN_MIN_SIZE, COLUMN_SIZE } from '../constants/styles';
import { getFutureDateInputProps } from '../utils/dateConstraints';

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getRowId = (row, index) => {
  const id =
    row?.id ??
    row?.internal_id ??
    row?.transaction_id ??
    row?.line_id ??
    row?.sonumber ??
    row?.name;
  return id ? `ats_dash_${String(id)}_${index ?? 0}` : `ats_dash_row_${index ?? 0}`;
};

const normalizeLocations = (payload) => {
  if (!payload) return [];
  const arr = Array.isArray(payload)
    ? payload
    : typeof payload === 'object'
      ? Object.values(payload)
      : [];
  return arr
    .map((item) => {
      if (!item) return null;
      if (typeof item === 'string') return item;
      return item.name || item.location || item.location_name || null;
    })
    .filter((v, idx, self) => v && self.indexOf(v) === idx);
};

const buildRowsFromBuckets = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return { rows: [], counts: { all: 0, sc: 0, no_sc: 0, not_picked: 0 } };
  }

  const rows = [];
  const counts = {
    all: 0,
    sc: 0,
    no_sc: 0,
    not_picked: 0,
  };

  Object.entries(payload).forEach(([bucketKey, value]) => {
    if (!Array.isArray(value)) return;
    const bucket = bucketKey.toLowerCase();

    if (bucket === 'sc') counts.sc += value.length;
    if (bucket === 'no_sc') counts.no_sc += value.length;
    if (bucket === 'not_picked') counts.not_picked += value.length;

    value.forEach((row) => {
      rows.push({
        ...row,
        status_bucket: bucketKey,
      });
    });
  });

  counts.all = rows.length;

  return { rows, counts };
};

const AtsDashboard = () => {
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState('');
  const [shipDate, setShipDate] = useState(
    () => localStorage.getItem('ats-dashboard-ship-date') || formatDateInput(new Date())
  );
  const [statusFilter, setStatusFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ all: 0, sc: 0, no_sc: 0, not_picked: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);

  // Load locations on mount
  useEffect(() => {
    let mounted = true;
    const loadLocations = async () => {
      try {
        const resp = await api.get(endpoints.locations);
        const payload = resp?.data?.data ?? resp?.data ?? resp;
        const normalized = normalizeLocations(payload);
        if (!mounted) return;
        setLocations(normalized);

        const storedLocation = localStorage.getItem('ats-dashboard-location');
        if (storedLocation && normalized.includes(storedLocation)) {
          setLocation(storedLocation);
          return;
        }

        const defaultLoc =
          normalized.find((loc) => loc.toLowerCase().includes('flemington')) || normalized[0] || '';
        setLocation(defaultLoc || '');
        if (defaultLoc) {
          localStorage.setItem('ats-dashboard-location', defaultLoc);
        }
      } catch (e) {
        console.error('Failed to load locations for ATS Dashboard:', e);
      }
    };

    loadLocations();
    return () => {
      mounted = false;
    };
  }, []);

  // Load ATS dashboard data when filters or refreshCount change
  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      if (!location || !shipDate) {
        setRows([]);
        setCounts({ all: 0, sc: 0, no_sc: 0, not_picked: 0 });
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params = {
          location,
          ship_date: shipDate,
        };
        const resp = await api.get(endpoints.atsDashboard, { params });
        const payload = resp?.data?.data ?? resp?.data ?? {};
        const { rows: mergedRows, counts: nextCounts } = buildRowsFromBuckets(payload);
        if (!mounted) return;
        setRows(mergedRows);
        setCounts(nextCounts);
      } catch (e) {
        if (!mounted) return;
        console.error('Error fetching ATS Dashboard data:', e);
        setError(e?.response?.data?.message || e?.message || 'Failed to load dashboard data');
        setRows([]);
        setCounts({ all: 0, sc: 0, no_sc: 0, not_picked: 0 });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [location, shipDate, refreshCount]);

  const handleApplyFilters = useCallback(() => {
    if (location) {
      localStorage.setItem('ats-dashboard-location', location);
    }
    if (shipDate) {
      localStorage.setItem('ats-dashboard-ship-date', shipDate);
    }
    setRefreshCount((c) => c + 1);
  }, [location, shipDate]);

  const filteredRows = useMemo(() => {
    if (statusFilter === 'all') return rows;
    const bucketKey =
      statusFilter === 'not_picked'
        ? 'not_picked'
        : statusFilter === 'no_ship_confirm'
          ? 'no_sc'
          : statusFilter;
    return rows.filter((row) => String(row.status_bucket || '').toLowerCase() === bucketKey);
  }, [rows, statusFilter]);

  const columns = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const keySet = new Set();
    rows.forEach((row) => Object.keys(row || {}).forEach((k) => keySet.add(k)));
    return Array.from(keySet).map((key) => ({
      accessorKey: key,
      header: key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined || value === '') return '-';
        if (typeof value === 'number') return Math.round(value);
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      },
    }));
  }, [rows]);

  const exportValueTransform = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE';
    if (Array.isArray(val)) return val.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join('; ');
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const [initialColumnVisibility] = useState(() => {
    try {
      const stored = localStorage.getItem('ats-dashboard-column-visibility');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const handleColumnVisibilityChange = useCallback((next) => {
    try {
      localStorage.setItem('ats-dashboard-column-visibility', JSON.stringify(next));
    } catch {}
  }, []);

  const gridHook = useDataGridProTable({
    data: filteredRows,
    loading,
    error,
    title: 'ATS Dashboard',
    columns,
    onDataRefresh: () => setRefreshCount((c) => c + 1),
    getRowId,
    exportValueTransform,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    initialColumnVisibility,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    muiTableContainerProps: { sx: { height: '100%', minHeight: 0 } },
    actionButtons: [
      <Box key="filters" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="ats-location-label">Location</InputLabel>
          <Select
            labelId="ats-location-label"
            value={location}
            label="Location"
            onChange={(e) => setLocation(e.target.value)}
            sx={{ '& .MuiSelect-select': { fontSize: '0.8125rem', py: 0.75 } }}
          >
            {locations.map((loc) => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Ship Date"
          type="date"
          value={shipDate}
          onChange={(e) => setShipDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={getFutureDateInputProps()}
          sx={{ minWidth: 170, '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.75 } }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="ats-status-filter-label">Type</InputLabel>
          <Select
            labelId="ats-status-filter-label"
            value={statusFilter}
            label="Type"
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ '& .MuiSelect-select': { fontSize: '0.8125rem', py: 0.75 } }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="no_ship_confirm">No Ship Confirm</MenuItem>
            <MenuItem value="not_picked">Not Picked</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          onClick={handleApplyFilters}
          sx={{ fontSize: '0.8125rem', height: '32px', textTransform: 'none', px: 2 }}
        >
          Apply
        </Button>
      </Box>,
    ],
  });

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Card sx={{ flex: '1 1 160px', minWidth: 160 }}>
            <CardContent sx={{ py: 1.25, px: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                All
              </Typography>
              <Typography variant="h6" sx={{ m: 0 }}>
                {counts.all}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 160px', minWidth: 160 }}>
            <CardContent sx={{ py: 1.25, px: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                No Ship Confirm
              </Typography>
              <Typography variant="h6" sx={{ m: 0 }}>
                {counts.no_sc}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 160px', minWidth: 160 }}>
            <CardContent sx={{ py: 1.25, px: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Not Picked
              </Typography>
              <Typography variant="h6" sx={{ m: 0 }}>
                {counts.not_picked}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <MuiXDataGridSurface hook={gridHook} DataGridPro={DataGridPro} />
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default AtsDashboard;

